import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Calendar, AlertTriangle, Repeat, ShieldCheck, Paperclip, ListChecks, Maximize2, Settings } from "lucide-react";
import { listarTarefas, Tarefa, atualizarTarefa } from "../../services/tarefas";
import { TarefaDrawer } from "../tarefas/TarefaDrawer";
import { urgenciaTarefa, diasRestantes, formatarPrazo } from "../../utils/urgencia";
import { tarefaPodeVencer } from "../../utils/tarefaVencida";
import { avisoToast } from "../../utils/avisoToast";
import { listarTags, Tag } from "../../services/tags";
import { TagChip } from "../../components/TagChip";
import { listarColunasProjeto, mesclarColunasComBase, ColunaCustom } from "../../services/projetoColunas";
import { listarColunasKanban } from "../../services/colunasKanban";
import { registrarAjusteComAnexos } from "../../services/ajustes";
import { RegistrarAjusteModal, DadosAjuste } from "../../components/RegistrarAjusteModal";
import { ColunasKanbanModal } from "./ColunasKanbanModal";
import { useAuth } from "../../hooks/useAuth";
import { useDragToScroll } from "../../hooks/useDragToScroll";
import { KanbanTopScrollbar } from "../../components/KanbanTopScrollbar";

/**
 * Kanban embutido na página de detalhe do projeto. Usa o mesmo modelo de
 * arrastar-e-soltar do KanbanBoard standalone, sem header — espera estar
 * dentro de outra UI que já tem o título e botões. Drag muda status.
 */
export function ProjetoKanbanEmbed({ projetoId, podeArrastar = true, kanbanFullHref }: {
  projetoId: string;
  podeArrastar?: boolean;
  kanbanFullHref?: string;
}) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const podeEditarColunas = can(["gestor", "admin"]);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [showConfigColunas, setShowConfigColunas] = useState(false);

  const { data: tarefas = [] } = useQuery({
    queryKey: ["tarefas", { projetoId }],
    queryFn: () => listarTarefas({ projetoId }),
    enabled: !!projetoId,
  });
  const { data: tagsCatalog = [] } = useQuery({
    queryKey: ["tags", "ativas"],
    queryFn: () => listarTags(),
  });
  // Customização visual das colunas — só pra ESTE projeto. Sempre mescla com defaults.
  const { data: colunasCustom = [] } = useQuery({
    queryKey: ["projeto-colunas", projetoId],
    queryFn: () => listarColunasProjeto(projetoId),
    enabled: !!projetoId,
  });
  const { data: colunasGlobais = [] } = useQuery({
    queryKey: ["colunas-kanban", "ativas"],
    queryFn: () => listarColunasKanban(),
  });
  const base: ColunaCustom[] = colunasGlobais.map((c) => ({ status: c.chave, label: c.label, cor: c.cor, emoji: c.emoji }));
  const colunas = mesclarColunasComBase(base, colunasCustom);

  const moverM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Tarefa> }) => atualizarTarefa(id, dados),
    onSuccess: (serverTask: any, variables: any) => {
      qc.invalidateQueries({ queryKey: ["tarefas", { projetoId }] });
      qc.invalidateQueries({ queryKey: ["projeto", projetoId] });
      if (variables?.dados?.status === "concluido" && serverTask?.status === "aguardando_aprovacao") {
        avisoToast("Enviado para Aprovação interna", `"${serverTask.titulo}" precisa da aprovação de um gestor/admin para ser finalizada.`, serverTask.id);
      }
    },
  });

  const porColuna: Record<string, Tarefa[]> = {};
  for (const c of colunas) porColuna[c.status] = [];
  for (const t of tarefas) {
    if (!porColuna[t.status]) porColuna[t.status] = [];
    porColuna[t.status].push(t);
  }

  // Rolagem lateral do quadro: barra no topo + arrastar o fundo (grab-to-pan).
  // Sem auto-scroll no drag AQUI: o container que rola é um div simples (não o
  // Droppable), então rolar durante o drag faria o card cair na coluna errada
  // — ver review jul/2026. O auto-scroll no drag fica só no quadro de tarefas.
  const boardRef = useRef<HTMLDivElement>(null);
  const pan = useDragToScroll(boardRef);

  function onDragEnd(result: DropResult) {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const novoStatus = destination.droppableId;
    const t = tarefas.find((x) => x.id === draggableId);
    if (!t) return;

    const colunaDest = (porColuna[novoStatus] || []).filter((x) => x.id !== draggableId);
    const acima = colunaDest[destination.index - 1];
    const abaixo = colunaDest[destination.index];
    let novaOrdem: number;
    if (!acima && !abaixo) novaOrdem = 0;
    else if (!acima) novaOrdem = (abaixo!.ordemKanban ?? 0) - 1000;
    else if (!abaixo) novaOrdem = (acima.ordemKanban ?? 0) + 1000;
    else novaOrdem = ((acima.ordemKanban ?? 0) + (abaixo.ordemKanban ?? 0)) / 2;

    const dados: Partial<Tarefa> = { ordemKanban: novaOrdem };
    if (t.status !== novoStatus) dados.status = novoStatus;
    if (novoStatus === "ajustes" && t.status !== "ajustes") {
      setAjustePendente({ id: draggableId, dados });
      return;
    }
    moverM.mutate({ id: draggableId, dados });
  }

  const [ajustePendente, setAjustePendente] = useState<{ id: string; dados: Partial<Tarefa> } | null>(null);
  const [salvandoAjuste, setSalvandoAjuste] = useState(false);
  async function confirmarAjuste(d: DadosAjuste) {
    if (!ajustePendente) return;
    setSalvandoAjuste(true);
    try {
      // Move PRIMEIRO; só registra o ajuste se o move deu certo — evita "ajuste
      // fantasma" (registrado num card que ficou fora da coluna Ajustes).
      await moverM.mutateAsync(ajustePendente);
      const { arquivos, ...dadosAjuste } = d;
      const { falhas } = await registrarAjusteComAnexos(ajustePendente.id, dadosAjuste, arquivos);
      setAjustePendente(null); // fecha só no sucesso
      if (falhas.length) alert(`Ajuste registrado, mas ${falhas.length} anexo(s) não subiram: ${falhas.join(", ")}. Reabra o card e use "+ arquivo".`);
    } catch (e: any) {
      alert(e?.response?.data?.error?.message || "Não foi possível mover o card / registrar o ajuste. Tente de novo.");
    } finally {
      setSalvandoAjuste(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Tarefas <span className="text-xs font-normal text-slate-400">({tarefas.length})</span>
        </h3>
        <div className="flex items-center gap-3">
          {podeEditarColunas && (
            <button
              type="button"
              onClick={() => setShowConfigColunas(true)}
              className="text-xs text-slate-500 hover:text-slate-700 hover:underline inline-flex items-center gap-1"
              title="Personalizar colunas deste kanban"
            >
              <Settings size={12} /> personalizar
            </button>
          )}
          {kanbanFullHref && (
            <Link
              to={kanbanFullHref}
              className="text-xs text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center gap-1"
              title="Abrir Kanban em tela cheia"
            >
              <Maximize2 size={12} /> tela cheia
            </Link>
          )}
        </div>
      </div>

      <KanbanTopScrollbar targetRef={boardRef} />
      <DragDropContext onDragEnd={onDragEnd}>
        <div ref={boardRef} onMouseDown={pan.onMouseDown} className="flex gap-3 overflow-x-auto kanban-x-hidden">
          {colunas.map((col) => (
            <div
              key={col.status}
              className="rounded-xl border-t-4 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 w-[250px] shrink-0"
              style={{ borderTopColor: col.cor }}
            >
              <div className="px-3 py-2 flex items-center justify-between gap-2">
                <h4 className="font-semibold text-xs uppercase tracking-wide text-slate-600 dark:text-slate-300 inline-flex items-center gap-1 truncate">
                  {col.emoji && <span>{col.emoji}</span>}
                  <span className="truncate">{col.label}</span>
                </h4>
                <span className="text-xs text-slate-400 tabular-nums shrink-0">{porColuna[col.status]?.length || 0}</span>
              </div>
              <Droppable droppableId={col.status} isDropDisabled={!podeArrastar}>
                {(provided, snap) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    // overscroll-y-contain: no eixo X a cadeia tem que ficar livre pro
                    // swipe do dedo em cima dos cards rolar o quadro (ver TarefasKanban).
                    className={`kanban-scroll p-2 space-y-2 min-h-[100px] max-h-[360px] overflow-y-auto overscroll-y-contain transition-colors ${snap.isDraggingOver ? "bg-blue-50/50 dark:bg-blue-900/20" : ""}`}
                  >
                    {porColuna[col.status]?.map((t, i) => (
                      <CardTarefa key={t.id} t={t} index={i} podeArrastar={podeArrastar} tagsCatalog={tagsCatalog} onAbrir={() => setDrawerId(t.id)} />
                    ))}
                    {provided.placeholder}
                    {(!porColuna[col.status] || porColuna[col.status].length === 0) && (
                      <p className="text-[11px] text-slate-300 dark:text-slate-600 text-center py-2 italic">vazio</p>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {ajustePendente && (
        <RegistrarAjusteModal onConfirmar={confirmarAjuste} onCancelar={() => setAjustePendente(null)} salvando={salvandoAjuste} />
      )}
      {drawerId && <TarefaDrawer tarefaId={drawerId} onDuplicada={(novaId) => setDrawerId(novaId)} onClose={() => setDrawerId(null)} />}
      {showConfigColunas && (
        <ColunasKanbanModal
          projetoId={projetoId}
          customsAtuais={colunasCustom}
          onClose={() => setShowConfigColunas(false)}
        />
      )}
    </div>
  );
}

function CardTarefa({ t, index, podeArrastar, tagsCatalog, onAbrir }: { t: Tarefa; index: number; podeArrastar: boolean; tagsCatalog: Tag[]; onAbrir: () => void }) {
  const urg = urgenciaTarefa(t);
  const dias = diasRestantes(t.prazo);
  const subTotal = t.subitens?.length || 0;
  const subOk = t.subitens?.filter((s) => s.concluido).length || 0;
  return (
    <Draggable draggableId={t.id} index={index} isDragDisabled={!podeArrastar}>
      {(provided, snap) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={onAbrir}
          className={`bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 cursor-pointer hover:shadow-md transition-shadow overflow-hidden ${snap.isDragging ? "shadow-xl rotate-1" : ""}`}
        >
          <div className={`h-1 ${urg.bg}`} />
          <div className="p-2.5">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium line-clamp-2 text-slate-800 dark:text-slate-100">{t.titulo}</p>
              <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded shrink-0 ${urg.badge}`}>
                {urg.texto}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500 dark:text-slate-400 flex-wrap">
              <span className="flex items-center gap-0.5">
                <Calendar size={10} />
                {formatarPrazo(t)}
                {tarefaPodeVencer(t.status) && (
                  <span className={dias < 0 ? "text-red-600 font-semibold" : ""}>
                    {dias < 0 ? ` (${-dias}d atras.)` : dias === 0 ? " (hoje)" : ` (${dias}d)`}
                  </span>
                )}
              </span>
              {t.recorrencia && <Repeat size={10} />}
              {t.aprovadorId && <ShieldCheck size={10} />}
              {(t.anexos?.length || 0) > 0 && (
                <span className="flex items-center gap-0.5"><Paperclip size={10} /> {t.anexos!.length}</span>
              )}
              {subTotal > 0 && (
                <span className="flex items-center gap-0.5"><ListChecks size={10} /> {subOk}/{subTotal}</span>
              )}
            </div>
            {t.etiquetas && t.etiquetas.trim() && (
              <div className="flex flex-wrap gap-0.5 mt-1.5">
                {t.etiquetas.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).slice(0, 3).map((slug) => (
                  <TagChip key={slug} tag={tagsCatalog.find((tg) => tg.slug === slug)} slug={slug} tamanho="xs" />
                ))}
              </div>
            )}
            {t.atribuido && (
              <p className="text-[10px] text-slate-600 dark:text-slate-300 mt-1 truncate">{t.atribuido.nome}</p>
            )}
            {tarefaPodeVencer(t.status) && new Date(t.prazo) < new Date() && (
              <p className="text-[10px] text-red-600 font-medium flex items-center gap-0.5 mt-0.5">
                <AlertTriangle size={9} /> Vencida
              </p>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}
